import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TAX_FREE_THRESHOLD } from '@/api/payments/paymentsService'
import { AppDialog } from '@/components/AppDialog'
import { Divider } from '@/components/Divider'
import UniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { PKG_SHRINK_FEE, PKG_UNITE_FEE_PER_PKG } from '@/helpers/pkgUtils'
import { cn } from '@/lib/utils'

import { UniteShrinkablePathPicker, type UniteShrinkPath } from './UniteShrinkablePathPicker'

export interface UniteConfirmPayload {
  shouldShrink?: boolean
  userComment: string
}

interface UniteConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (payload: UniteConfirmPayload) => void
  isLoading?: boolean
  isFreeUnite?: boolean
  isShrinkable?: boolean
}

const isValidChar = (char: string): boolean => {
  const charCode = char.charCodeAt(0)
  return charCode >= 32 && charCode <= 126
}

export const UniteConfirmationDialog: FC<UniteConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  isFreeUnite,
  isShrinkable = false,
}) => {
  const { t } = useTranslation()
  const [comment, setComment] = useState('')
  const [warehouseNotesOpen, setWarehouseNotesOpen] = useState<string>('')
  const [shrinkPath, setShrinkPath] = useState<UniteShrinkPath>('standard')
  const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setComment('')
      setWarehouseNotesOpen('')
      setShrinkPath('standard')
      setShowInvalidCharTooltip(false)
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
        tooltipTimeoutRef.current = null
      }
    }
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value
    const filteredValue = inputValue
      .split('')
      .filter(char => isValidChar(char))
      .join('')

    if (filteredValue.length < inputValue.length) {
      setShowInvalidCharTooltip(true)
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowInvalidCharTooltip(false)
      }, 3000)
    } else {
      setShowInvalidCharTooltip(false)
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }

    setComment(filteredValue)
  }
  const onSetWarehouseNotesOpen = (value: string) => {
    setWarehouseNotesOpen(value)
    if (!value) {
      setComment('')
    }
  }
  const submit = (shouldShrink?: boolean) => {
    onConfirm({ shouldShrink, userComment: comment.trim() })
  }

  const warehouseNotesAccordion = (
    <Accordion type='single' collapsible className='w-full' value={warehouseNotesOpen} onValueChange={onSetWarehouseNotesOpen}>
      <AccordionItem value='warehouse-notes' className='border-0'>
        <AccordionTrigger className='text-text-secondary py-2 text-xs hover:no-underline'>
          {t('unite.addWarehouseNoteButton')}
        </AccordionTrigger>
        <AccordionContent className='flex flex-col gap-2 pb-0'>
          <TooltipProvider>
            <Tooltip open={showInvalidCharTooltip}>
              <TooltipTrigger asChild>
                <div className='w-full'>
                  <Textarea
                    id='unite-warehouse-notes'
                    value={comment}
                    onChange={handleInputChange}
                    maxLength={500}
                    className='w-full'
                    supportingText={t('unite.warehouseNotesSupportingText')}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side='top' dir='rtl'>
                {t('unite.invalidCharsTooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <p className='text-text-secondary text-xs leading-relaxed'>{t('unite.warehouseNotesDisclaimer')}</p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )

  return (
    <AppDialog
      alwaysDialog
      fullScreenDialog='mobile'
      isOpen={isOpen}
      onOpenChange={onClose}
      className={cn('!max-w-lg', isShrinkable && '!max-w-3xl')}
    >
      <div className='flex flex-col items-center gap-6'>
        <div className='flex flex-col items-center gap-4'>
          <UniteIcon className='text-icon-primary size-6 shrink-0' />
          <h2 className='text-text-primary text-center text-lg font-medium'>{t('unite.uniteTitle')}</h2>
        </div>

        {isShrinkable ? (
          <>
            <div className='flex w-full flex-col items-center gap-6 px-1'>
              <div className='text-text-secondary text-center text-base leading-6'>
                <p className='mb-1'>{t('unite.shrinkableIntro1')}</p>
                <p>{t('unite.shrinkableIntro2')}</p>
              </div>
              {isFreeUnite ? (
                <p className='text-text-secondary text-center text-sm leading-5'>{t('unite.shrinkableFreeUniteNote')}</p>
              ) : null}
              <UniteShrinkablePathPicker
                path={shrinkPath}
                onPathChange={setShrinkPath}
                isFreeUnite={isFreeUnite}
                uniteFee={PKG_UNITE_FEE_PER_PKG}
                shrinkFee={PKG_SHRINK_FEE}
              />
              <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-2 py-1.5'>
                <p className='text-center text-sm leading-5'>{t('unite.shrinkableTaxPill', { threshold: TAX_FREE_THRESHOLD })}</p>
              </div>

              <p className='text-text-secondary text-xs'>{t('unite.uniteCancelHint')}</p>
              {warehouseNotesAccordion}
            </div>
            <Divider />
            <div className='flex items-center justify-center'>
              <Button disabled={isLoading} onClick={() => submit(shrinkPath === 'plus')} size='lg'>
                {t('unite.shrinkableContinue')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className='flex w-full flex-col items-center gap-6 px-1'>
              <div className='text-text-secondary text-center text-sm leading-5'>
                <p className='mb-1'>{t('unite.uniteDesc1')}</p>
                <p className='mb-1'>{t('unite.uniteDesc2')}</p>
                <p>{t('unite.uniteDesc3', { threshold: TAX_FREE_THRESHOLD })}</p>
              </div>
              <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-3 py-2'>
                <p className='text-center text-xs'>{isFreeUnite ? t('unite.freeUniteMessage') : t('unite.paidUniteMessage')}</p>
              </div>
              <p className='text-text-secondary text-xs'>{t('unite.uniteCancelHint')}</p>
            </div>
            <Divider />
            <div className='flex items-center justify-center gap-6'>
              <Button disabled={isLoading} onClick={() => submit()} size='lg'>
                {t('unite.uniteConfirmButton')}
              </Button>
            </div>
          </>
        )}
      </div>
    </AppDialog>
  )
}
